import { useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import Cookies from "js-cookie";
import Loader from "../../shared/loaders/Loader";
import { environment } from "../../../config/environments/environment";
import { appUrls } from "../../../config/api-config/appUrls";
import {AUTH_CONSTANTS} from "../../../config/auth/authConstants";
import {setCookie} from "../../../../utils/setCookie";
import {
  setMixpanelEventForSignUpRequestFail,
  setMixpanelEventForAuth0SignUpSuccess,
  setMixpanelEventForAuth0SignInSuccess,
} from "../../../common/mixpanel-events/sign-up";

import { setTokenIssuedAt } from "../../../config/auth/useRefreshToken";

interface Auth0TokenExchangeProps {
  onTokenExchangeComplete?: () => void;
}

export default function Auth0TokenExchange({ onTokenExchangeComplete }: Auth0TokenExchangeProps) {
  const { isAuthenticated, getIdTokenClaims, isLoading } = useAuth0();
  const hasAuthToken = !!Cookies.get("auth_token");
  const [isTokenExchangeInProgress, setIsTokenExchangeInProgress] = useState(false);
  const [hasAttemptedExchange, setHasAttemptedExchange] = useState(false);
  const exchangeAttemptRef = useRef(false);

  useEffect(() => {

    if (exchangeAttemptRef.current) {
      return;
    }

    if (isAuthenticated && !isLoading && !Cookies.get("auth_token")) {
      exchangeAttemptRef.current = true;
      setIsTokenExchangeInProgress(true);
      setHasAttemptedExchange(true);

      getIdTokenClaims().then(claims => {

        if (!claims || !claims.__raw) {
          setIsTokenExchangeInProgress(false);
          exchangeAttemptRef.current = false;
          return;
        }

        // Extract the provider from the claims (e.g., google-oauth2, windowslive)
        const provider = claims?.['https://convay.com/connection'] || claims?.sub?.split('|')[0];
        if (provider) {
          let providerName = '';
          if (provider.includes('google')) {
            providerName = 'google';
          } else if (provider.includes('windowslive') || provider.includes('microsoft')) {
            providerName = 'microsoft';
          }
          if (providerName) {
            localStorage.setItem('auth_provider', providerName);
          }
        }

        const isSignUpContext = localStorage.getItem('auth0_signup_context') === 'true';

        const requestData = {
          id_token: claims.__raw,
          is_signup: isSignUpContext,
          is_only_google: false,
          invitation_link: localStorage.getItem('invitationLink') || null
        };

        const url = environment.organizationSettingsApi + appUrls.organization.auth0Callback;
        axios.post(url, requestData)  // Remove withCredentials: true
        .then((response) => {
          // Extract JWT token from response body
          if (response.data.success && response.data.accessToken) {
            // Check if user is organization creator/owner
            if (response.data.user) {
              if (response.data.isNewUser && response.data.user.role == "Owner" && !localStorage.getItem('invitationLink')) {
                localStorage.setItem("creator-sign-up", "creator-sign-up");
              }
            }

            localStorage.removeItem('invitationLink');

            // Set the auth token cookie manually
            const accessTokenExpiry = new Date(Date.now() + (AUTH_CONSTANTS.ACCESS_TOKEN_EXPIRY_MINUTES * 60 * 1000));
            const refreshTokenExpiry = new Date(Date.now() + (AUTH_CONSTANTS.REFRESH_TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000));


            setCookie("auth_token", response.data.accessToken, { expires: accessTokenExpiry });
            setCookie("refresh_token", response.data.refreshToken, { expires: refreshTokenExpiry });
            setTokenIssuedAt();

            try {
              const userEmail = response.data.user?.email ?? "";
              if (isSignUpContext) {
                const provider = localStorage.getItem('auth_provider') || 'Auth0';
                setMixpanelEventForAuth0SignUpSuccess(userEmail, provider);
                localStorage.removeItem('auth0_signup_context');
              } else {
                if(response.data.isNewUser === true) {
                  const provider = localStorage.getItem('auth_provider') || 'Auth0';
                  setMixpanelEventForAuth0SignUpSuccess(userEmail, provider);
                }
                setMixpanelEventForAuth0SignInSuccess(
                  userEmail,
                  response.data.user?.display_name ?? "",
                  response.data.user?.organization_name ?? "",
                );
              }
            } catch (mixpanelError) {
              console.warn("Mixpanel event tracking failed:", mixpanelError);
            }

            if (onTokenExchangeComplete) {
              onTokenExchangeComplete();
            }

            // Set state and redirect - use setTimeout to ensure redirect happens after state update
            setIsTokenExchangeInProgress(false);
            setTimeout(() => {
              window.location.href = "/home";
            }, 0);
          } else {
            setIsTokenExchangeInProgress(false);
            exchangeAttemptRef.current = false;
          }
        })
        .catch(err => {
          // Safely access error response properties with null checks
          const userEmail = err.response?.data?.user?.email || "unknown";
          const errorMessage = err.response?.data?.message || err.message || "Unknown error occurred";

          setMixpanelEventForSignUpRequestFail(userEmail, errorMessage);

          console.error("Error details:", {
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            config: {
              url: err.config?.url,
              method: err.config?.method,
              headers: err.config?.headers
            }
          });

          setIsTokenExchangeInProgress(false);
          exchangeAttemptRef.current = false;

          if (isSignUpContext) {
            localStorage.removeItem('auth0_signup_context');
          }
        });
      }).catch(err => {
        setIsTokenExchangeInProgress(false);
        exchangeAttemptRef.current = false;
      });
    } else {
      console.error("Token exchange conditions not met:", {
        isAuthenticated,
        isLoading,
        hasAuthToken: !!Cookies.get("auth_token"),
        reason: !isAuthenticated ? "Not authenticated" :
                isLoading ? "Still loading" :
                hasAuthToken ? "Already has auth token" : "Unknown"
      });
    }
  }, [isAuthenticated, isLoading, getIdTokenClaims, onTokenExchangeComplete]);

  if (isTokenExchangeInProgress) {
    const isSignUpContext = localStorage.getItem('auth0_signup_context') === 'true';
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Loader circleStyle={{ background: "#ECEDF0" }} />
        </div>
          {/* <p style={{ marginTop: '20px', color: '#666' }}>
            {isSignUpContext ? 'Creating your account...' : 'Signing you in...'}
          </p>
          <p style={{ marginTop: '10px', color: '#999', fontSize: '12px' }}>
            Please wait while we process your authentication...
          </p> */}
        </div>
    );
  }

  return null;
}
